import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  CloudOff,
  Database,
  GraduationCap,
  Layers,
  PauseCircle,
  ListChecks,
  Loader2,
  MapPin,
  Network,
  RefreshCw,
  Send,
  ShieldAlert,
  Sparkles,
  Store,
  Tags,
  Users,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { PublishToNetworkDialog } from '../../components/admin/PublishToNetworkDialog'
import { ErrorMessage } from '../../components/ErrorMessage'
import type {
  AdminProviderResponse,
  BulkActionResponse,
  SignalsAudienceStatus,
  SignalsNetworkStatus,
} from '../../types/api'
import { AdminHeader } from './adminAccess'
import { AdminHero, HeroBadge } from './AdminHero'

/** One step of a card's how-it-works strip. */
type Step = { icon: ReactNode; label: string }

const RESYNC_STEPS: Step[] = [
  { icon: <Database size={16} />, label: 'Service records' },
  { icon: <Sparkles size={16} />, label: 'Recompute' },
  { icon: <Tags size={16} />, label: 'Category columns' },
  { icon: <MapPin size={16} />, label: 'Map filters' },
]

const PUBLISH_STEPS: Step[] = [
  { icon: <Users size={16} />, label: 'Pick audience' },
  { icon: <Layers size={16} />, label: 'Publish in slices' },
  { icon: <Network size={16} />, label: 'Signals-DPG' },
  { icon: <MapPin size={16} />, label: 'Dot on the map' },
]

/** The whole-directory jobs that used to sit in the Provider report's top bar, with the numbers behind them. */
export function AdminSyncPage() {
  const navigate = useNavigate()
  const [providers, setProviders] = useState<AdminProviderResponse[] | null>(null)
  const [network, setNetwork] = useState<SignalsNetworkStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<{ at: Date; res: BulkActionResponse } | null>(null)
  const [publishOpen, setPublishOpen] = useState(false)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const loadStats = useCallback(() => {
    const fail = (fallback: string) => (err: unknown) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        signOut()
        return
      }
      setError(apiErrorMessage(err, fallback))
    }
    adminApi.providers({}).then(setProviders).catch(fail('Could not load the provider numbers'))
    adminApi.signalsStatus().then(setNetwork).catch(fail('Could not load the Signals-DPG numbers'))
  }, [signOut])

  useEffect(loadStats, [loadStats])

  const stats = useMemo(() => {
    if (!providers) return null
    const total = providers.length
    const withCategory = providers.filter((p) => p.categories.length > 0).length
    const withSub = providers.filter((p) => p.subCategories.length > 0).length
    const verified = providers.filter((p) => p.verificationStatus === 'VERIFIED').length
    const suspended = providers.filter((p) => p.verificationStatus === 'SUSPENDED').length
    return {
      total,
      withCategory,
      withSub,
      unassigned: total - withCategory,
      verified,
      suspended,
      unverified: total - verified - suspended,
    }
  }, [providers])

  async function reproject() {
    setBusy(true)
    setError(null)
    try {
      const res = await adminApi.reprojectProviders()
      setLastRun({ at: new Date(), res })
      loadStats()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not re-sync providers'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="sync" onSignOut={signOut} width="max-w-7xl">
        <button onClick={loadStats} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6">
        <AdminHero
          icon={<RefreshCw size={26} />}
          eyebrow="Sync center"
          title="Keep the map in step"
          blurb="Line every provider's directory columns up with their real service data, and put the right people on the Signals-DPG network so they show up on the map."
        >
          <HeroBadge>
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Safe to re-run
          </HeroBadge>
          <HeroBadge>
            <ShieldAlert size={13} className="text-amber-600" /> Admins only
          </HeroBadge>
        </AdminHero>

        <ErrorMessage message={error} />

        {/* Signals-DPG network */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Network size={16} className="text-teal-600" />
            <h2 className="text-lg font-extrabold text-ink-900">On Signals-DPG</h2>
            <span className="text-xs text-ink-500">- only people synced here show up on the map</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <NetworkCard
              icon={<Store size={18} />}
              title="Providers"
              tone="from-orange-500 to-rose-500"
              status={network?.providers}
            />
            <NetworkCard
              icon={<GraduationCap size={18} />}
              title="Seekers"
              tone="from-sky-500 to-blue-600"
              status={network?.seekers}
            />
          </div>
        </section>

        {/* Stat tiles */}
        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile
            icon={<Users size={18} />}
            label="Providers"
            value={stats?.total}
            tone="from-sky-500 to-indigo-500"
            hint="In the directory"
          />
          <StatTile
            icon={<Tags size={18} />}
            label="Category set"
            value={stats?.withCategory}
            tone="from-emerald-500 to-teal-500"
            hint={stats ? `${pct(stats.withCategory, stats.total)}% of providers` : undefined}
          />
          <StatTile
            icon={<AlertTriangle size={18} />}
            label="Unassigned"
            value={stats?.unassigned}
            tone="from-orange-500 to-rose-500"
            hint="Can't be published yet"
          />
          <StatTile
            icon={<BadgeCheck size={18} />}
            label="Verified"
            value={stats?.verified}
            tone="from-dot-500 to-sun-400"
            hint={stats ? `${pct(stats.verified, stats.total)}% of providers` : undefined}
          />
        </section>

        {/* Infographics */}
        <section className="grid gap-4 lg:grid-cols-5">
          <div className="rounded-2xl bg-white p-5 shadow-card lg:col-span-2">
            <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
              <ListChecks size={16} className="text-emerald-600" /> Category coverage
            </h2>
            <p className="text-xs text-ink-500">How much of the directory the map can filter on.</p>
            <div className="mt-4 flex items-center justify-around gap-4">
              <Ring
                value={stats ? pct(stats.withCategory, stats.total) : 0}
                label="Category"
                color="#10b981"
                loading={!stats}
              />
              <Ring
                value={stats ? pct(stats.withSub, stats.total) : 0}
                label="Sub category"
                color="#6366f1"
                loading={!stats}
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-card lg:col-span-3">
            <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
              <BadgeCheck size={16} className="text-dot-600" /> Verification mix
            </h2>
            <p className="text-xs text-ink-500">Suspended providers are kept off the network.</p>
            <StackedBar
              loading={!stats}
              total={stats?.total ?? 0}
              parts={[
                { label: 'Verified', value: stats?.verified ?? 0, bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
                { label: 'Unverified', value: stats?.unverified ?? 0, bar: 'bg-sun-400', dot: 'bg-sun-400' },
                { label: 'Suspended', value: stats?.suspended ?? 0, bar: 'bg-rose-500', dot: 'bg-rose-500' },
              ]}
            />
          </div>
        </section>

        {/* Actions */}
        <section className="grid gap-4 md:grid-cols-2">
          <ActionCard
            accent="from-violet-600 via-indigo-600 to-sky-500"
            icon={<Sparkles size={22} />}
            title="Re-sync from service data"
            blurb="Recomputes every provider's Category and Sub Category from their actual service records - fixes anyone whose map columns drifted out of sync."
            steps={RESYNC_STEPS}
            stepTone="bg-indigo-50 text-indigo-700 ring-indigo-200"
          >
            <button
              onClick={reproject}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:brightness-110 disabled:opacity-60"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {busy ? 'Re-syncing...' : 'Re-sync now'}
            </button>
            {lastRun && <RunResult run={lastRun} />}
          </ActionCard>

          <ActionCard
            accent="from-emerald-500 via-teal-500 to-cyan-500"
            icon={<Send size={22} />}
            title="Publish to network"
            blurb="Choose providers or seekers to publish to the Signals-DPG network - the map only lists people published there. Anyone already on it is left alone."
            steps={PUBLISH_STEPS}
            stepTone="bg-emerald-50 text-emerald-700 ring-emerald-200"
          >
            <button
              onClick={() => setPublishOpen(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:brightness-110 disabled:opacity-60"
            >
              <Send size={15} /> Publish to network...
            </button>
            {stats && stats.unassigned > 0 && (
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-800 ring-1 ring-orange-200">
                <AlertTriangle size={14} className="mt-px shrink-0" />
                {stats.unassigned} provider{stats.unassigned === 1 ? ' has' : 's have'} no category and
                will be skipped - assign one on the Provider report first.
              </p>
            )}
          </ActionCard>
        </section>
      </main>

      {publishOpen && (
        <PublishToNetworkDialog
          onClose={() => {
            setPublishOpen(false)
            loadStats()
          }}
        />
      )}
    </div>
  )
}

const pct = (part: number, total: number) => (total === 0 ? 0 : Math.round((part / total) * 100))

/** One audience on the network: synced, not synced yet, held back - as a ring, a bar and three counts. */
function NetworkCard({
  icon,
  title,
  tone,
  status,
}: {
  icon: ReactNode
  title: string
  tone: string
  status: SignalsAudienceStatus | undefined
}) {
  const total = status?.total ?? 0
  const parts = [
    { label: 'Synced', value: status?.onNetwork, bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', icon: <CheckCircle2 size={14} /> },
    { label: 'Not synced', value: status?.notOnNetwork, bar: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50', icon: <CloudOff size={14} /> },
    { label: 'Held back', value: status?.heldBack, bar: 'bg-ink-300', text: 'text-ink-600', bg: 'bg-ink-50', icon: <PauseCircle size={14} /> },
  ]
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone}`} aria-hidden />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br ${tone} text-white shadow-md`}>{icon}</span>
          <div>
            <h3 className="text-base font-extrabold text-ink-900">{title}</h3>
            <p className="text-xs text-ink-500">
              {status ? `${total.toLocaleString()} in all` : 'Loading...'}
            </p>
          </div>
        </div>
        <SmallRing value={status ? pct(status.onNetwork, total) : 0} loading={!status} />
      </div>

      <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-ink-100">
        {status &&
          parts.map((p) =>
            p.value ? (
              <div
                key={p.label}
                className={`${p.bar} h-full transition-all duration-700`}
                style={{ width: `${(p.value / Math.max(total, 1)) * 100}%`, minWidth: 4 }}
                title={`${p.label}: ${p.value.toLocaleString()}`}
              />
            ) : null,
          )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {parts.map((p) => (
          <div key={p.label} className={`rounded-xl ${p.bg} px-3 py-2`}>
            <div className={`flex items-center gap-1 text-[11px] font-semibold ${p.text}`}>
              {p.icon} {p.label}
            </div>
            <div className="text-xl font-extrabold tabular-nums text-ink-900">
              {p.value === undefined ? '-' : p.value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-ink-400">
        Not synced can be published with Publish to network. Held back are suspended or waiting on consent.
      </p>
    </div>
  )
}

function SmallRing({ value, loading }: { value: number; loading: boolean }) {
  const r = 40
  const c = 2 * Math.PI * r
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 100 100" className="h-20 w-20 -rotate-90" role="img" aria-label={`${value}% synced`}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#fed7aa" strokeWidth="12" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="#10b981"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={loading ? c : c * (1 - value / 100)}
          style={{ transition: 'stroke-dashoffset 900ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center leading-none">
        <div>
          <div className="text-base font-extrabold text-ink-900">{loading ? '-' : `${value}%`}</div>
          <div className="mt-0.5 text-[9px] font-semibold uppercase text-ink-500">synced</div>
        </div>
      </div>
    </div>
  )
}

function StatTile({
  icon,
  label,
  value,
  tone,
  hint,
}: {
  icon: ReactNode
  label: string
  value: number | undefined
  tone: string
  hint?: string
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-4 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone}`} aria-hidden />
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">{label}</span>
        <span className={`grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br ${tone} text-white shadow-md`}>
          {icon}
        </span>
      </div>
      <div className="mt-2 text-3xl font-extrabold tracking-tight text-ink-900">
        {value === undefined ? <span className="inline-block h-8 w-16 animate-pulse rounded bg-ink-100" /> : value.toLocaleString()}
      </div>
      {hint && <div className="mt-0.5 text-xs text-ink-500">{hint}</div>}
    </div>
  )
}

/** A donut gauge for one percentage. */
function Ring({ value, label, color, loading }: { value: number; label: string; color: string; loading: boolean }) {
  const r = 42
  const c = 2 * Math.PI * r
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-32 w-32">
      <svg viewBox="0 0 100 100" className="h-32 w-32 -rotate-90" role="img" aria-label={`${label}: ${value}%`}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#efece4" strokeWidth="10" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={loading ? c : c * (1 - value / 100)}
          style={{ transition: 'stroke-dashoffset 900ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-2xl font-extrabold text-ink-900">
        {loading ? '-' : `${value}%`}
      </div>
      </div>
      <div className="text-xs font-semibold text-ink-600">{label}</div>
    </div>
  )
}

function StackedBar({
  parts,
  total,
  loading,
}: {
  parts: { label: string; value: number; bar: string; dot: string }[]
  total: number
  loading: boolean
}) {
  return (
    <div className="mt-5">
      <div className="flex h-5 w-full overflow-hidden rounded-full bg-ink-100">
        {!loading &&
          parts.map((p) =>
            p.value > 0 ? (
              <div
                key={p.label}
                className={`${p.bar} h-full transition-all duration-700`}
                style={{ width: `${(p.value / Math.max(total, 1)) * 100}%` }}
                title={`${p.label}: ${p.value}`}
              />
            ) : null,
          )}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {parts.map((p) => (
          <div key={p.label} className="rounded-xl bg-ink-50 p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-600">
              <span className={`h-2.5 w-2.5 rounded-full ${p.dot}`} /> {p.label}
            </div>
            <div className="mt-1 text-xl font-extrabold text-ink-900">{loading ? '-' : p.value.toLocaleString()}</div>
            <div className="text-[11px] text-ink-500">{loading ? '' : `${pct(p.value, total)}%`}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ActionCard({
  accent,
  icon,
  title,
  blurb,
  steps,
  stepTone,
  children,
}: {
  accent: string
  icon: ReactNode
  title: string
  blurb: string
  steps: Step[]
  stepTone: string
  children: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className={`relative bg-gradient-to-r ${accent} px-5 py-4 text-white`}>
        <div className="pointer-events-none absolute -right-6 -top-10 h-28 w-28 rounded-full bg-white/15 blur-xl" aria-hidden />
        <div className="relative flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/20 ring-1 ring-white/30">{icon}</span>
          <h2 className="text-lg font-extrabold tracking-tight">{title}</h2>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm text-ink-600">{blurb}</p>

        <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400">How it works</div>
        <ol className="mt-2 flex flex-wrap items-center gap-1.5">
          {steps.map((s, i) => (
            <li key={s.label} className="flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${stepTone}`}>
                {s.icon} {s.label}
              </span>
              {i < steps.length - 1 && <ArrowRight size={14} className="text-ink-300" />}
            </li>
          ))}
        </ol>

        <div className="mt-5">{children}</div>
      </div>
    </div>
  )
}

function RunResult({ run }: { run: { at: Date; res: BulkActionResponse } }) {
  const { succeeded, failed } = run.res
  const total = succeeded + failed.length
  return (
    <div className="mt-4 rounded-xl bg-ink-50 p-3 ring-1 ring-ink-200">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold text-ink-900">Last run</span>
        <span className="text-ink-500">{run.at.toLocaleTimeString()}</span>
      </div>
      <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-ink-200">
        <div className="bg-emerald-500" style={{ width: `${(succeeded / Math.max(total, 1)) * 100}%` }} />
        <div className="bg-rose-500" style={{ width: `${(failed.length / Math.max(total, 1)) * 100}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
          <CheckCircle2 size={13} /> {succeeded} updated
        </span>
        {failed.length > 0 && (
          <span className="inline-flex items-center gap-1 font-semibold text-rose-700">
            <XCircle size={13} /> {failed.length} failed
          </span>
        )}
      </div>
      {failed.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[11px] text-rose-700">
          {failed.slice(0, 3).map((f) => <li key={f.participantId}>{f.error}</li>)}
        </ul>
      )}
    </div>
  )
}
